import { useEffect } from 'react'
import { useMutation, useLazyQuery, gql } from '@apollo/client'
import clsx from 'clsx'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify'

import { GET_OFFERINGS, UPDATE_AGENDA_ITEM_MUTATION } from '../services/admin'

const GET_AGENDA_ITEMS = gql`
  query GetAgendaItemsWithDetail {
    agendaItems(first: 50) {
      edges {
        node {
          databaseId
          order
          postId
          title
          lock
          detail {
            knowledgeCheck {
              lock
            }
          }
        }
      }
    }
  }
`

const AdminUnlockContent = () => {
  const [
    getAgendaItems,
    {
      loading: agendaItemsDataLoading,
      error: agendaItemsDataError,
      data: agendaItemsData,
    },
  ] = useLazyQuery(GET_AGENDA_ITEMS, {
    fetchPolicy: 'no-cache',
  })
  const [
    getOfferings,
    {
      loading: offeringsDataLoading,
      error: offeringsDataError,
      data: offeringsData,
    },
  ] = useLazyQuery(GET_OFFERINGS, {
    fetchPolicy: 'no-cache',
  })
  const [updateAgendaItemMutation, { loading: updating }] = useMutation(
    UPDATE_AGENDA_ITEM_MUTATION
  )

  useEffect(() => {
    getAgendaItems()
    getOfferings()
  }, [getAgendaItems, getOfferings])

  /**
   * Unlock the content of the specified `day`
   *
   * @param {string} type - Type: content or reflection
   * @param {string} title - Agenda title
   * @param {number} agendaId - Agenda database ID
   * @param {boolean} lockStatus - Lock status to assign to the content
   */
  const updateLockStatus = async (
    type = '',
    title = '',
    agendaId,
    lockStatus = false
  ) => {
    if (type === '') {
      toast.warning(`Unknown type`)
      return
    }

    try {
      let variables = {}

      if (type === 'content') {
        variables = {
          clientMutationId: uuidv4(),
          id: agendaId,
          lock: lockStatus,
        }
      } else if (type === 'reflection') {
        variables = {
          clientMutationId: uuidv4(),
          id: agendaId,
          detail: {
            knowledgeCheck: {
              lock: lockStatus,
            },
          },
        }
      }
      await updateAgendaItemMutation({
        variables,
      })

      // Reload data
      getAgendaItems()

      toast.success(
        `${title} ${
          type === 'content'
            ? 'content'
            : type === 'reflection'
            ? 'reflection point'
            : ''
        } ${lockStatus ? 'disabled' : 'enabled'}`
      )
    } catch (err) {
      console.error(
        `Unable to lock/unlock ${title} ${
          type === 'content'
            ? 'content'
            : type === 'reflection'
            ? 'reflection point'
            : ''
        }`
      )
      toast.error(
        `Unable to lock/unlock ${title} ${
          type === 'content'
            ? 'content'
            : type === 'reflection'
            ? 'reflection point'
            : ''
        }`
      )
    }
  }

  const offerings = offeringsData?.offerings?.edges?.map((edge) => edge.node)
  const numOfferings = offerings?.length ?? 0

  const agendaItems = agendaItemsData?.agendaItems?.edges
    ?.map((edge) => edge.node)
    // Don't show Preparation in the `unlock` admin panel
    .filter((node) => node.postId !== 'preparation')
  if (agendaItems) {
    agendaItems.sort((a, b) => a.order - b.order)
  }

  return (
    <div>
      {/** Daily unlocks */}
      <div className="mb-4 text-sm font-bold text-flame">
        Daily Content Unlocks
      </div>
      <p>
        The buttons below unlock the interactive agendas for the specified day.
      </p>
      <div className="mt-9 px-2">
        {(agendaItemsDataLoading && offeringsDataLoading) ||
        (!agendaItemsData && !offeringsData) ? (
          <LoadingSkeleton />
        ) : (
          <div className="flex flex-row flex-wrap items-center justify-between">
            {agendaItems?.map((agenda) => {
              const lock = agenda?.lock ?? true

              return (
                <button
                  key={`${agenda.postId}${agenda.title}`}
                  type="button"
                  disabled={numOfferings === 0 || updating}
                  className={clsx(
                    'rounded-full px-6 py-3 leading-none text-white transition-colors',
                    lock
                      ? 'bg-skin-button-accent hover:bg-skin-button-accent-hover'
                      : 'bg-skin-fill hover:opacity-80',
                    updating || numOfferings === 0
                      ? 'pointer-events-none cursor-not-allowed'
                      : 'cursor-pointer active:translate-y-[0.75px] active:scale-[0.99]'
                  )}
                  onClick={() => {
                    updateLockStatus(
                      'content',
                      agenda.title,
                      agenda.databaseId,
                      !lock
                    )
                  }}
                >
                  {lock ? 'Unlock' : 'Lock'} {agenda.title}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/** Daily reflection point unlocks */}
      <div className="mb-4 mt-16 text-sm font-bold text-flame">
        Daily Reflection Point Unlocks
      </div>
      <p>
        The buttons below unlock the Reflection Point of the specified day.
        Learners will earn points after all 5 questions in the Reflection Point
        are answered.
      </p>
      <div className="mt-9 px-2">
        {(agendaItemsDataLoading && offeringsDataLoading) ||
        (!agendaItemsData && !offeringsData) ? (
          <LoadingSkeleton />
        ) : (
          <div className="flex flex-row flex-wrap items-center justify-between">
            {agendaItems?.map((agenda) => {
              const lock = agenda.detail?.knowledgeCheck?.lock ?? true

              return (
                <button
                  key={`${agenda.postId}${agenda.title}`}
                  type="button"
                  disabled={numOfferings === 0 || updating}
                  className={clsx(
                    'rounded-full px-6 py-3 leading-none text-white transition-colors',
                    lock
                      ? 'bg-skin-button-accent hover:bg-skin-button-accent-hover'
                      : 'bg-skin-fill hover:opacity-80',
                    updating || numOfferings === 0
                      ? 'pointer-events-none cursor-not-allowed'
                      : 'cursor-pointer active:translate-y-[0.75px] active:scale-[0.99]'
                  )}
                  onClick={() => {
                    updateLockStatus(
                      'reflection',
                      agenda.title,
                      agenda.databaseId,
                      !lock
                    )
                  }}
                >
                  {lock ? 'Unlock' : 'Lock'} {agenda.title}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

const LoadingSkeleton = () => {
  return (
    <div role="status" className="animate-pulse">
      <div className="flex w-full items-center justify-between">
        <div className="rounded-full bg-gray-300 px-[60px] py-[18px] leading-none dark:bg-gray-700"></div>
        <div className="rounded-full bg-gray-300 px-[60px] py-[18px] leading-none dark:bg-gray-700"></div>
        <div className="rounded-full bg-gray-300 px-[60px] py-[18px] leading-none dark:bg-gray-700"></div>
        <div className="rounded-full bg-gray-300 px-[60px] py-[18px] leading-none dark:bg-gray-700"></div>
      </div>
      <span className="sr-only">Loading...</span>
    </div>
  )
}

export default AdminUnlockContent
